import React, { useState } from 'react';
import {
  Box,
  Grid,
  TextField,
  Button,
  Typography,
  Paper,
  Alert,
  MenuItem,
  IconButton,
  styled
} from '@mui/material';
import {
  Favorite as HardshipIcon,
  Close as CloseIcon
} from '@mui/icons-material';

const StyledPaper = styled(Paper)(({ theme }) => ({
  backgroundColor: 'rgba(255, 255, 255, 0.05)',
  padding: theme.spacing(3),
  borderRadius: theme.spacing(1),
}));

const HardshipGrantForm = ({ onSubmit, projects, isSubmitting }) => {
  const [formData, setFormData] = useState({
    applicantName: '',
    email: '',
    phone: '',
    projectId: '',
    amount: '',
    reason: '',
    urgency: 'standard',
    impact: '',
    attachments: []
  });
  
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      await onSubmit(formData);
      setSuccess('Hardship grant application submitted successfully!');
      resetForm();
    } catch (err) {
      setError(err.message);
    }
  };

  const resetForm = () => {
    setFormData({
      applicantName: '',
      email: '',
      phone: '',
      projectId: '',
      amount: '',
      reason: '',
      urgency: 'standard',
      impact: '',
      attachments: []
    });
  };

  return (
    <Box component="form" onSubmit={handleSubmit}>
      <Grid container spacing={3}>
        <Grid item xs={12}>
          <Typography variant="h6" sx={{ color: '#00BCD4', mb: 2 }}>
            <HardshipIcon sx={{ mr: 1 }} />
            Hardship Grant Application
          </Typography>
        </Grid>

        {error && (
          <Grid item xs={12}>
            <Alert 
              severity="error"
              action={
                <IconButton size="small" onClick={() => setError(null)}>
                  <CloseIcon fontSize="small" />
                </IconButton>
              }
            >
              {error}
            </Alert>
          </Grid>
        )}

        {success && (
          <Grid item xs={12}>
            <Alert 
              severity="success"
              action={
                <IconButton size="small" onClick={() => setSuccess(null)}>
                  <CloseIcon fontSize="small" />
                </IconButton>
              }
            >
              {success}
            </Alert>
          </Grid>
        )}

        <Grid item xs={12}>
          <TextField
            fullWidth
            label="Applicant Name"
            name="applicantName"
            value={formData.applicantName}
            onChange={handleChange}
            required
          />
        </Grid>

        <Grid item xs={12} md={6}>
          <TextField
            fullWidth
            label="Email"
            name="email"
            type="email"
            value={formData.email}
            onChange={handleChange}
            required
          />
        </Grid>

        <Grid item xs={12} md={6}>
          <TextField
            fullWidth
            label="Phone"
            name="phone"
            value={formData.phone}
            onChange={handleChange}
            required
          />
        </Grid>

        <Grid item xs={12}>
          <TextField
            fullWidth
            label="Project"
            name="projectId"
            select
            value={formData.projectId}
            onChange={handleChange}
            required
          >
            {projects.map((project) => (
              <MenuItem key={project._id} value={project._id}>
                {project.name}
              </MenuItem>
            ))}
          </TextField>
        </Grid>

        <Grid item xs={12}>
          <TextField
            fullWidth
            label="Amount Requested"
            name="amount"
            type="number"
            value={formData.amount}
            onChange={handleChange}
            required
          />
        </Grid>

        <Grid item xs={12}>
          <TextField
            fullWidth
            label="Reason for Hardship"
            name="reason"
            multiline
            rows={4}
            value={formData.reason}
            onChange={handleChange}
            required
          />
        </Grid>

        <Grid item xs={12}>
          <TextField
            fullWidth
            label="Urgency Level"
            name="urgency"
            select
            value={formData.urgency}
            onChange={handleChange}
            required
          >
            <MenuItem value="immediate">Immediate (24-48 hours)</MenuItem>
            <MenuItem value="urgent">Urgent (1 week)</MenuItem>
            <MenuItem value="standard">Standard (2-3 weeks)</MenuItem>
          </TextField>
        </Grid>

        <Grid item xs={12}>
          <TextField
            fullWidth
            label="Expected Impact"
            name="impact"
            multiline
            rows={4}
            value={formData.impact}
            onChange={handleChange}
            required
          />
        </Grid>

        <Grid item xs={12}>
          <Button
            fullWidth
            type="submit"
            variant="contained"
            disabled={isSubmitting}
            sx={{
              backgroundColor: '#00BCD4',
              '&:hover': {
                backgroundColor: '#00ACC1',
              },
              '&:disabled': {
                backgroundColor: 'rgba(0, 188, 212, 0.5)',
              }
            }}
          >
            {isSubmitting ? 'Submitting...' : 'Submit Application'}
          </Button>
        </Grid>
      </Grid>
    </Box>
  );
};

export default HardshipGrantForm; 